import { TENANT_BILLING_CONFIGURATION_STATUS } from '../billing/billing.interface';
import type {
  CreateTenantBillingConfiguration,
  TenantBillingConfiguration,
  TenantFreeTrial,
} from '../billing/billing.interface';
import { baseDocumentFactory } from './base-document.factory';
import { billingPlanFactory } from './billing-plan.factory';
import { ObjectFactory } from './object.factory';

/** Stable ObjectId-like id for the default embedded plan snapshot. */
const DEFAULT_PLAN_ID = 'bbbbbbbbbbbbbbbbbbbbbbbb';

export class TenantBillingConfigurationFactory extends ObjectFactory<TenantBillingConfiguration> {
  protected getDefaults(): TenantBillingConfiguration {
    return {
      ...baseDocumentFactory.build(),
      from: new Date('2026-01-01T00:00:00.000Z'),
      overrides: {},
      plan: billingPlanFactory.buildSummary({ id: DEFAULT_PLAN_ID, name: 'standard' }),
      status: TENANT_BILLING_CONFIGURATION_STATUS.ACTIVE,
      to: new Date('2026-12-31T23:59:59.999Z'),
      trial: null,
    };
  }

  /** `plan` is resolved server-side from `planId` at creation time — never client-supplied. */
  buildCreate(overrides: Partial<CreateTenantBillingConfiguration> = {}): CreateTenantBillingConfiguration {
    const { planId: planIdOverride, ...buildOverrides } = overrides;
    const { from, overrides: configOverrides, plan, to, trial } = this.build(buildOverrides);
    return { from, overrides: configOverrides, planId: planIdOverride ?? plan?.id ?? DEFAULT_PLAN_ID, to, trial };
  }

  /**
   * A plan-less pending trial row — the shape automatically created on tenant account validation,
   * before a plan has been chosen (`PHA-706`). `status` is always `pending` and `plan` always `null`.
   */
  buildPlanLessTrial(
    trial: TenantFreeTrial,
    overrides: Partial<Omit<TenantBillingConfiguration, 'plan' | 'status' | 'trial'>> = {},
  ): TenantBillingConfiguration {
    return this.build({
      ...overrides,
      plan: null,
      status: TENANT_BILLING_CONFIGURATION_STATUS.PENDING,
      trial,
    });
  }
}

export const tenantBillingConfigurationFactory = new TenantBillingConfigurationFactory();
